import { Repeat } from 'lucide-react';

import {
  cadenceSentence,
  formatCurrency,
  formatDate,
  recurringActions,
  recurringStatusKey,
} from '@splitfiesta/core';
import type { RecurringViewer } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { RecurringRuleDto } from '@splitfiesta/shared';
import { Button, Card } from '@splitfiesta/ui';

interface RecurringRulesListProps {
  rules: RecurringRuleDto[];
  /** Null until the group and user are known; no actions are offered then. */
  viewer: RecurringViewer | null;
  onPause: (rule: RecurringRuleDto) => void;
  onResume: (rule: RecurringRuleDto) => void;
  onStop: (rule: RecurringRuleDto) => void;
  /** The rule a pause, resume or stop is in flight for. */
  pendingRuleId?: string | null;
}

const STATUS_CLASSES: Record<ReturnType<typeof recurringStatusKey>, string> = {
  active: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  pausedUser: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  pausedPro:
    'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  pausedMemberLeft:
    'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  ended: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

export function RecurringRulesList({
  rules,
  viewer,
  onPause,
  onResume,
  onStop,
  pendingRuleId = null,
}: RecurringRulesListProps) {
  const { t, i18n } = useTranslation();

  if (rules.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <Repeat aria-hidden="true" className="h-10 w-10 text-gray-400" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('groups:recurring.empty')}
          </p>
        </div>
      </Card>
    );
  }

  const stop = (rule: RecurringRuleDto) => {
    // An ended rule only goes from the list: nothing is left to stop.
    if (
      recurringStatusKey(rule) !== 'ended' &&
      !window.confirm(
        t('groups:recurring.stopConfirm', { title: rule.template.title })
      )
    ) {
      return;
    }
    onStop(rule);
  };

  return (
    <ul data-testid="recurring-rules-list" className="space-y-3">
      {rules.map(rule => {
        const statusKey = recurringStatusKey(rule);
        const actions = viewer
          ? recurringActions(rule, viewer)
          : { pause: false, resume: false, stop: false };
        const sentence = cadenceSentence(rule.cadence);
        const busy = pendingRuleId === rule.id;
        return (
          <li key={rule.id} data-testid="recurring-rule-row">
            <Card>
              <div className="space-y-2">
                <div className="flex flex-row flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h2 className="min-w-[45%] flex-1 break-words font-semibold text-gray-900 dark:text-gray-100">
                    {rule.template.title}
                  </h2>
                  <span className="ml-auto font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(
                      rule.template.amount,
                      rule.template.currency,
                      i18n.language
                    )}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t(sentence.key, { count: sentence.count })}
                  {statusKey !== 'ended' && (
                    <>
                      {' · '}
                      {t('groups:recurring.next', {
                        date: formatDate(rule.nextDate, i18n.language),
                      })}
                    </>
                  )}
                </p>
                <p>
                  <span
                    className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[statusKey]}`}
                  >
                    {t(`groups:recurring.status.${statusKey}`)}
                  </span>
                </p>
                {(actions.pause || actions.resume || actions.stop) && (
                  <div className="flex flex-wrap justify-end gap-2 pt-1">
                    {actions.pause && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-11"
                        onClick={() => onPause(rule)}
                        disabled={busy}
                        data-testid="recurring-rule-pause"
                      >
                        {t('groups:recurring.action.pause')}
                      </Button>
                    )}
                    {actions.resume && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-11"
                        onClick={() => onResume(rule)}
                        disabled={busy}
                        data-testid="recurring-rule-resume"
                      >
                        {t('groups:recurring.action.resume')}
                      </Button>
                    )}
                    {actions.stop && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-11 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={() => stop(rule)}
                        disabled={busy}
                        data-testid="recurring-rule-stop"
                      >
                        {t(
                          statusKey === 'ended'
                            ? 'groups:recurring.action.delete'
                            : 'groups:recurring.action.stop'
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </li>
        );
      })}
    </ul>
  );
}
